import { createFileRoute, Link, useBlocker, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import { toast } from 'sonner';
import {
  LABEL_KINDS,
  LABEL_TITLES,
  LABEL_TOKENS,
  defaultLabelDesign,
  type LabelDesign,
  type LabelDesigns,
  type LabelElement,
  type LabelKind,
  type LabelTemplateHistory,
  type LabelTemplateRevision,
  type SavedLabelDesign,
} from '@kk-pos/shared';
import { api, ApiError, errorMessage } from '@/lib/api/client';
import { useAtLeast } from '@/lib/auth';
import { dateTime } from '@/lib/format';
import { useShortcutHint } from '@/app/StatusLine';
import { Page, PageHeader, PageBody, Panel, ErrorState, Skeleton } from '@/ui/Panel';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Callout } from '@/ui/Callout';
import { Field, Input, Select, Checkbox } from '@/ui/Field';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { SubNav, SubNavItem } from '@/ui/Filters';
// Matches BwLogo.png bundled with the label station.
import stationLogo from '@/features/labels/station-logo.png';
import {
  ADD_GROUPS,
  FIELD_NAMES,
  LABEL_USED_FOR,
  LOGO_ASPECT,
  PAPER_SIZES,
  boundElement,
  choiceLabel,
  contentGroups,
  defaultSample,
  designError,
  fieldListOrder,
  fieldName,
  neverPrints,
  nextFitSize,
  textFitFloor,
  overlapPairs,
  parseFontSize,
  parsePaperInches,
  placeNewField,
  repositionPaper,
  resizeLogo,
  resizePaper,
  ruleChoices,
  ruleWords,
  sampleShowing,
  sampleText,
  sampleValues,
  sampleVisibility,
  sourceHint,
  tokenOf,
  unknownTokens,
  type AddChoice,
  type Sample,
} from '@/features/labels/designer';

export const Route = createFileRoute('/_app/settings_/label-templates')({
  staticData: { crumb: 'Label templates' },
  validateSearch: (s: Record<string, unknown>): { kind: LabelKind } => ({
    kind: LABEL_KINDS.find((k) => k === s.kind) ?? 'shelf',
  }),
  component: LabelTemplatesScreen,
});

const TEMPLATES_KEY = ['label-templates'] as const;
const historyKey = (kind: LabelKind) => ['label-templates', kind, 'revisions'] as const;
const SAVED_STATUS = 'Saved. Stations get it at their next Sync.';

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT' ||
    target.isContentEditable
  );
}

function LabelTemplatesScreen() {
  const { kind } = Route.useSearch();
  const navigate = useNavigate();
  const query = useQuery({
    queryKey: TEMPLATES_KEY,
    queryFn: () => api.get<LabelDesigns>('/labels/templates'),
    refetchOnWindowFocus: false,
  });
  const setUp = query.data ? LABEL_KINDS.filter((k) => query.data[k]).length : null;
  return (
    <Page>
      <PageHeader
        eyebrow="Settings"
        title="Label templates"
        subtitle={
          setUp === null
            ? 'Design once here. Stations get it at their next Sync.'
            : `${setUp} of ${LABEL_KINDS.length} label types set up. Stations get changes at their next Sync.`
        }
        actions={
          <Button asChild variant="ghost">
            <Link to="/settings">Back to settings</Link>
          </Button>
        }
        tabs={
          <SubNav>
            {LABEL_KINDS.map((k) => (
              <SubNavItem
                key={k}
                active={kind === k}
                onClick={() =>
                  void navigate({ to: '/settings/label-templates', search: { kind: k } })
                }
              >
                {LABEL_TITLES[k]}
                {query.data && !query.data[k] ? (
                  <Badge tone="warn">Not set up</Badge>
                ) : null}
              </SubNavItem>
            ))}
          </SubNav>
        }
      />
      <PageBody>
        {query.isLoading ? (
          <Skeleton className="h-80" />
        ) : query.isError ? (
          <ErrorState
            message={errorMessage(query.error)}
            onRetry={() => void query.refetch()}
          />
        ) : query.data ? (
          <Designer key={kind} kind={kind} initial={query.data[kind]} />
        ) : null}
      </PageBody>
    </Page>
  );
}

type Gesture = {
  x: number;
  y: number;
  element: LabelElement;
  index: number;
  resize: boolean;
  /** The design before the gesture; becomes an Undo step on the first real move. */
  before: LabelDesign;
  moved: boolean;
};

function Designer({
  kind,
  initial,
}: {
  kind: LabelKind;
  initial: SavedLabelDesign | null;
}) {
  const canEdit = useAtLeast('manager');
  const [draft, setDraft] = useState<LabelDesign>(
    () => initial?.design ?? defaultLabelDesign(kind)
  );
  const [saved, setSaved] = useState(initial);
  const [selected, setSelected] = useState<number | null>(0);
  const [past, setPast] = useState<LabelDesign[]>([]);
  const [future, setFuture] = useState<LabelDesign[]>([]);
  const [resetOpen, setResetOpen] = useState(false);
  const [reloadOpen, setReloadOpen] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<LabelTemplateRevision | null>(null);
  const [sizeDialog, setSizeDialog] = useState<{ w: string; h: string } | null>(null);
  const [addChoice, setAddChoice] = useState<AddChoice>('ProductName');
  const [sample, setSample] = useState<Sample>(() => defaultSample(kind));
  const [status, setStatus] = useState<{
    tone: 'positive' | 'warn';
    text: string;
  } | null>(null);
  const client = useQueryClient();
  // The latest draft, for a save started from a timer (see requestSave).
  const draftRef = useRef(draft);
  useLayoutEffect(() => {
    draftRef.current = draft;
  }, [draft]);
  // A text size typed but not yet applied (the box applies it on Enter or blur).
  // It counts as an unsaved change, so Save, Ctrl+S and the leave prompt see it.
  const [pendingSize, setPendingSize] = useState(false);

  const history = useQuery({
    queryKey: historyKey(kind),
    queryFn: () => api.get<LabelTemplateHistory>(`/labels/templates/${kind}/revisions`),
    refetchOnWindowFocus: false,
  });
  // A stored layout this version can't read still has a revision, so a save can
  // replace it (review row 16).
  const baseRevision = saved?.revision ?? history.data?.currentRevision ?? null;
  const unreadable = !saved && history.data?.currentReadable === false;

  const savedDesign = saved?.design ?? defaultLabelDesign(kind);
  const dirty = pendingSize || JSON.stringify(draft) !== JSON.stringify(savedDesign);
  // The saved layout, for the same timer-started save.
  const savedRef = useRef(saved);
  useLayoutEffect(() => {
    savedRef.current = saved;
  }, [saved]);
  const blocker = useBlocker({
    shouldBlockFn: () => dirty,
    enableBeforeUnload: dirty,
    withResolver: true,
  });

  function applySaved(result: SavedLabelDesign, message: string) {
    setSaved(result);
    setDraft(result.design);
    setPast([]);
    setFuture([]);
    setStatus({ tone: 'positive', text: message });
    // Write the result straight into the cache so switching tabs before a refetch
    // can't bring back the old layout (review row 13).
    client.setQueryData<LabelDesigns>(TEMPLATES_KEY, (old) =>
      old ? { ...old, [kind]: result } : old
    );
    void client.invalidateQueries({ queryKey: historyKey(kind) });
  }

  const save = useMutation({
    mutationFn: () =>
      api.put<SavedLabelDesign>(`/labels/templates/${kind}`, {
        revision: baseRevision,
        design: draftRef.current,
      }),
    onSuccess: (result) => {
      applySaved(result, SAVED_STATUS);
      toast.success('Template saved');
    },
    onError: (error) =>
      toast.error('Template was not saved', { description: errorMessage(error) }),
  });
  const restore = useMutation({
    mutationFn: (version: LabelTemplateRevision) =>
      api.post<SavedLabelDesign>(`/labels/templates/${kind}/restore`, {
        revision: baseRevision,
        restore: version.revision,
      }),
    onSuccess: (result, version) => {
      applySaved(
        result,
        `Restored the version saved ${dateTime(version.savedAt)}. Stations get it at their next Sync.`
      );
      setRestoreTarget(null);
      save.reset();
      toast.success('Version restored');
    },
    onError: (error) =>
      toast.error('Version was not restored', { description: errorMessage(error) }),
  });
  const reload = useMutation({
    mutationFn: () => api.get<LabelDesigns>('/labels/templates'),
    onSuccess: (result) => {
      client.setQueryData(TEMPLATES_KEY, result);
      void client.invalidateQueries({ queryKey: historyKey(kind) });
      setSaved(result[kind]);
      setDraft(result[kind]?.design ?? defaultLabelDesign(kind));
      setSelected(0);
      setPast([]);
      setFuture([]);
      setReloadOpen(false);
      save.reset();
      restore.reset();
      setStatus({ tone: 'positive', text: 'Latest saved template loaded.' });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const busy = save.isPending || reload.isPending || restore.isPending;
  const error = designError(draft);
  const canSave = canEdit && !error && (!saved || dirty) && !busy;
  const saveError = save.error ?? restore.error;
  const conflict = saveError instanceof ApiError && saveError.status === 409;

  const elements = draft.template.elements;
  const pairs = overlapPairs(draft);
  const overlapping = new Set(pairs.flat());
  const overlapNames = [
    ...new Set(
      pairs.map(([a, b]) => `${fieldName(elements[a]!)} and ${fieldName(elements[b]!)}`)
    ),
  ];
  const element = selected === null ? undefined : elements[selected];
  const scale = Math.min(160, 640 / draft.widthInches, 360 / draft.heightInches);
  const gesture = useRef<Gesture | null>(null);
  const customText = useRef<HTMLInputElement>(null);

  function change(next: LabelDesign) {
    setPast((h) => [...h.slice(-49), draft]);
    setFuture([]);
    setDraft(next);
    setStatus(null);
  }
  function undo() {
    const previous = past[past.length - 1];
    if (!previous || busy) return;
    setFuture((f) => [draft, ...f].slice(0, 50));
    setPast((h) => h.slice(0, -1));
    setDraft(previous);
    // Keep the current selection (review row 9); only drop it if the field is gone.
    setSelected((s) => (s !== null && s < previous.template.elements.length ? s : null));
  }
  function redo() {
    const next = future[0];
    if (!next || busy) return;
    setPast((h) => [...h.slice(-49), draft]);
    setFuture((f) => f.slice(1));
    setDraft(next);
    setSelected((s) => (s !== null && s < next.template.elements.length ? s : null));
  }
  function patchElement(patch: Partial<LabelElement>) {
    if (!element || selected === null) return;
    change({
      ...draft,
      template: {
        ...draft.template,
        elements: elements.map((e, i) =>
          i === selected ? boundElement({ ...e, ...patch }, draft) : e
        ),
      },
    });
  }
  function removeSelected() {
    if (selected === null || !element || busy) return;
    change({
      ...draft,
      template: {
        ...draft.template,
        elements: elements.filter((_, i) => i !== selected),
      },
    });
    setSelected(elements.length > 1 ? Math.max(0, selected - 1) : null);
  }
  function select(i: number) {
    setSelected(i);
    setSample((s) => sampleShowing(elements[i]?.visibleWhen, s));
  }
  function requestSave() {
    // Apply a text size still being typed (the box applies it on blur), then
    // decide from the committed draft whether there is anything to save.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    setTimeout(() => {
      const next = draftRef.current;
      const base = savedRef.current;
      const changed = !base || JSON.stringify(next) !== JSON.stringify(base.design);
      if (canEdit && changed && !designError(next)) save.mutate();
    }, 0);
  }

  // Standard shortcuts (review quick win 8). Held in a ref so the listener always
  // sees the current state without re-subscribing on every render.
  const onShortcut = useRef<(event: globalThis.KeyboardEvent) => void>(() => {});
  const shortcut = (event: globalThis.KeyboardEvent) => {
    const mod = event.ctrlKey || event.metaKey;
    if (document.querySelector('[role="dialog"]')) return;
    if (mod && event.key.toLowerCase() === 's') {
      event.preventDefault();
      if (canEdit && !busy && (canSave || pendingSize)) requestSave();
      return;
    }
    if (isTyping(event.target)) return;
    if (mod && event.key.toLowerCase() === 'z') {
      event.preventDefault();
      if (event.shiftKey) redo();
      else undo();
    } else if (mod && event.key.toLowerCase() === 'y') {
      event.preventDefault();
      redo();
    } else if ((event.key === 'Delete' || event.key === 'Backspace') && !mod) {
      // Only from the label itself or with nothing focused, so Backspace on a
      // button elsewhere on the page never removes a field by surprise.
      const onLabel =
        event.target === document.body ||
        (event.target instanceof Element &&
          event.target.closest('[data-testid="label-canvas"]') !== null);
      if (selected === null || !onLabel) return;
      event.preventDefault();
      removeSelected();
    } else if (event.key === 'Escape' && selected !== null) {
      setSelected(null);
    }
  };
  useLayoutEffect(() => {
    onShortcut.current = shortcut;
  });
  useEffect(() => {
    const listener = (event: globalThis.KeyboardEvent) => onShortcut.current(event);
    document.addEventListener('keydown', listener);
    return () => document.removeEventListener('keydown', listener);
  }, []);
  useShortcutHint([
    { keys: 'Del', label: 'Remove field' },
    { keys: 'Ctrl+Z', label: 'Undo' },
    { keys: 'Ctrl+Shift+Z', label: 'Redo' },
    { keys: 'Ctrl+S', label: 'Save' },
    { keys: 'Esc', label: 'Deselect' },
  ]);

  function pointerDown(
    event: PointerEvent<HTMLButtonElement>,
    index: number,
    resize: boolean
  ) {
    if (busy || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.focus();
    event.currentTarget.setPointerCapture(event.pointerId);
    setSelected(index);
    gesture.current = {
      x: event.clientX,
      y: event.clientY,
      element: elements[index]!,
      index,
      resize,
      before: draft,
      moved: false,
    };
  }
  function pointerMove(event: PointerEvent<HTMLButtonElement>) {
    const g = gesture.current;
    if (!g) return;
    if (!g.moved) {
      // A plain click only selects; the Undo step starts with real movement
      // (review row 9).
      if (Math.hypot(event.clientX - g.x, event.clientY - g.y) < 2) return;
      g.moved = true;
      setPast((h) => [...h.slice(-49), g.before]);
      setFuture([]);
    }
    const dx = Math.round(((event.clientX - g.x) / scale) * 100) / 100;
    const dy = Math.round(((event.clientY - g.y) / scale) * 100) / 100;
    setDraft((d) => {
      const next: LabelElement = g.resize
        ? g.element.type === 'Image'
          ? resizeLogo(g.element, dx, dy, d)
          : {
              ...g.element,
              width: Math.min(g.element.width + dx, d.widthInches - g.element.x),
              height: Math.min(g.element.height + dy, d.heightInches - g.element.y),
            }
        : { ...g.element, x: g.element.x + dx, y: g.element.y + dy };
      return {
        ...d,
        template: {
          ...d.template,
          elements: d.template.elements.map((e, i) =>
            i === g.index ? boundElement(next, d) : e
          ),
        },
      };
    });
    setStatus(null);
  }
  function endGesture() {
    gesture.current = null;
  }

  function add() {
    let added: LabelElement = {
      type: 'Text',
      x: 0.1,
      y: 0.1,
      width: Math.min(1.4, draft.widthInches - 0.2),
      height: 0.3,
      text: addChoice === 'custom' ? 'Your text' : `{${addChoice}}`,
      fontFamily: 'Arial',
      fontSize: 10,
      wrap: true,
      autoFit: true,
    };
    if (addChoice === 'barcode')
      added = {
        ...added,
        type: 'Barcode',
        text: undefined,
        height: 0.45,
        barcodePayloadToken: kind === 'weighed' ? 'WeightedBarcode' : 'PrimaryBarcode',
        barcodeFormat: 'Auto',
      };
    if (addChoice === 'logo') {
      const height = Math.min(0.55, draft.heightInches - 0.2);
      added = {
        ...added,
        type: 'Image',
        text: undefined,
        height,
        width: Math.round(height * LOGO_ASPECT * 100) / 100,
        imageSource: 'logo',
      };
    }
    if (addChoice === 'WicBadge') added.visibleWhen = 'WicEligible';
    if (addChoice === 'IngredientsLine' || addChoice === 'Ingredients')
      added.visibleWhen = 'HasIngredients';
    if (addChoice === 'AllergensLine' || addChoice === 'Allergens')
      added.visibleWhen = 'HasAllergens';
    const placement = placeNewField(added, draft);
    added = placement.element;
    const additions: LabelElement[] =
      addChoice === 'barcode' && kind === 'receipt'
        ? [
            {
              ...added,
              barcodePayloadToken: 'WeightedBarcode',
              visibleWhen: 'NeedsWeight',
            },
            {
              ...added,
              barcodePayloadToken: 'PrimaryBarcode',
              visibleWhen: '!NeedsWeight',
            },
          ]
        : [added];
    change({
      ...draft,
      template: { ...draft.template, elements: [...elements, ...additions] },
    });
    const index = elements.length + (sample.weighed || additions.length === 1 ? 0 : 1);
    setSelected(index);
    setSample((s) => sampleShowing(additions[index - elements.length]?.visibleWhen, s));
    if (!placement.placed)
      setStatus({
        tone: 'warn',
        text: 'No free space: drag it into place or choose a larger label.',
      });
  }

  function changeSize(mode: 'stretch' | 'keep') {
    if (!sizeDialog) return;
    const w = parsePaperInches(sizeDialog.w);
    const h = parsePaperInches(sizeDialog.h);
    if (w === null || h === null) return;
    change(mode === 'stretch' ? resizePaper(draft, w, h) : repositionPaper(draft, w, h));
    setSizeDialog(null);
  }

  const token = tokenOf(element);
  const contentChoice = token === 'Sku' ? 'ProductId' : (token ?? 'custom');
  const unknown = element?.type === 'Text' ? unknownTokens(element.text) : [];
  const values = sampleValues(sample);
  const sizeKey = `${draft.widthInches}x${draft.heightInches}`;
  const standardSize = PAPER_SIZES.some(([w, h]) => `${w}x${h}` === sizeKey);
  const savedLine = dirty
    ? 'Unsaved changes'
    : saved
      ? `Saved${saved.savedAt ? ` ${dateTime(saved.savedAt)}` : ''}${saved.savedBy ? ` by ${saved.savedBy}` : ''} · stations get it at their next Sync`
      : "Not set up — stations can't print this label type until it is saved.";
  const rules = ruleChoices(kind);
  const currentRule = element?.visibleWhen ?? '';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">
            {saved ? 'Your store template' : 'Start with this sample layout'}
            <span className="text-ink-muted ml-2 text-xs font-normal">
              Used for: {LABEL_USED_FOR[kind]}
            </span>
          </p>
          <p className="text-ink-muted text-xs" data-testid="saved-state">
            {savedLine}
          </p>
        </div>
        <div className="flex gap-2">
          <Button disabled={!past.length || busy} onClick={undo}>
            Undo
          </Button>
          <Button disabled={!future.length || busy} onClick={redo}>
            Redo
          </Button>
          <Button disabled={busy} onClick={() => setResetOpen(true)}>
            Start over
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!canSave}
            onClick={requestSave}
          >
            Save template
          </Button>
        </div>
      </div>
      {!canEdit ? (
        <Callout tone="info">
          Only a manager can save or restore label layouts. You can try changes here, but
          not save them.
        </Callout>
      ) : null}
      {unreadable ? (
        <Callout tone="warn">
          The saved {LABEL_TITLES[kind]} layout doesn't match this version&apos;s label
          format, so stations can&apos;t use it. Save this layout to replace it.
        </Callout>
      ) : null}
      {status ? (
        <p
          role="status"
          className={`rounded p-3 text-sm ${status.tone === 'warn' ? 'bg-warn-muted text-warn' : 'bg-positive-muted text-positive'}`}
        >
          {status.text}
        </p>
      ) : null}
      {saveError ? (
        <div role="alert" className="bg-negative-muted text-negative rounded p-3 text-sm">
          {errorMessage(saveError)}{' '}
          {conflict ? (
            <Button variant="link" onClick={() => setReloadOpen(true)}>
              Reload saved template
            </Button>
          ) : null}
        </div>
      ) : null}
      {error ? (
        <p role="alert" className="text-warn text-sm">
          {error}
        </p>
      ) : null}
      {pairs.length ? (
        <p role="status" className="text-warn bg-warn-muted rounded px-3 py-2 text-sm">
          {overlapNames.slice(0, 3).join('; ')}
          {overlapNames.length > 3 ? ` and ${overlapNames.length - 3} more` : ''} overlap.
          Drag them apart, shrink them, or choose a larger label size.
        </p>
      ) : null}
      <fieldset
        disabled={busy}
        className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1fr)_320px]"
      >
        <div className="min-w-0 space-y-4">
          <Panel
            title="Label preview"
            description="Click a field to edit it. Drag to move; use its corner handle to resize."
          >
            <div className="mb-3 flex flex-wrap items-end gap-3">
              <Field label="Label size" htmlFor="paper-size">
                <Select
                  id="paper-size"
                  value={sizeKey}
                  onChange={(e) => {
                    if (e.target.value === 'custom')
                      setSizeDialog({
                        w: String(draft.widthInches),
                        h: String(draft.heightInches),
                      });
                    else {
                      const [w, h] = e.target.value.split('x');
                      setSizeDialog({ w: w!, h: h! });
                    }
                  }}
                >
                  {PAPER_SIZES.map(([w, h]) => (
                    <option key={`${w}x${h}`} value={`${w}x${h}`}>
                      {w} × {h} in
                    </option>
                  ))}
                  {!standardSize ? (
                    <option value={sizeKey}>
                      {draft.widthInches} × {draft.heightInches} in
                    </option>
                  ) : null}
                  <option value="custom">Custom size…</option>
                </Select>
              </Field>
              <Field label="Add to label" htmlFor="add-field" className="min-w-0 flex-1">
                <div className="flex gap-2">
                  <Select
                    id="add-field"
                    className="min-w-0 flex-1"
                    value={addChoice}
                    onChange={(e) => setAddChoice(e.target.value as AddChoice)}
                  >
                    {ADD_GROUPS.map((g) => (
                      <optgroup key={g.label} label={g.label}>
                        {g.choices.map((c) => (
                          <option key={c} value={c}>
                            {choiceLabel(c, sample)}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </Select>
                  <Button
                    onClick={add}
                    disabled={
                      elements.length >
                      60 - (addChoice === 'barcode' && kind === 'receipt' ? 2 : 1)
                    }
                  >
                    Add
                  </Button>
                </div>
              </Field>
            </div>
            <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2">
              {kind === 'receipt' ? (
                <Checkbox
                  label="Sample: weighed item"
                  checked={sample.weighed}
                  onCheckedChange={(weighed) => setSample((s) => ({ ...s, weighed }))}
                />
              ) : null}
              {kind === 'weighed' ? (
                <Checkbox
                  label="Sample: prepared food sold by weight"
                  checked={sample.preparedByWeight}
                  onCheckedChange={(preparedByWeight) =>
                    setSample((s) => ({ ...s, preparedByWeight }))
                  }
                />
              ) : null}
              <Checkbox
                label="Show optional sample details"
                checked={sample.optional}
                onCheckedChange={(optional) => setSample((s) => ({ ...s, optional }))}
              />
            </div>
            <div className="bg-surface-sunken overflow-auto rounded border border-line p-5">
              <div
                data-testid="label-canvas"
                aria-label="Sample label"
                className="relative mx-auto shrink-0 shadow-md"
                style={{
                  width: draft.widthInches * scale,
                  height: draft.heightInches * scale,
                  background: 'white',
                  color: 'black',
                }}
              >
                {elements.map((e, i) => {
                  const visibility = sampleVisibility(e.visibleWhen, sample);
                  const hidden = visibility === 'hidden';
                  return (
                    <div
                      key={i}
                      aria-hidden={hidden || undefined}
                      style={{
                        position: 'absolute',
                        left: e.x * scale,
                        top: e.y * scale,
                        width: e.width * scale,
                        height: e.height * scale,
                        opacity: hidden ? 0 : visibility === 'dimmed' ? 0.2 : 1,
                        pointerEvents: hidden ? 'none' : undefined,
                      }}
                    >
                      <button
                        type="button"
                        aria-label={`Select ${fieldName(e)} ${i + 1}`}
                        aria-pressed={selected === i}
                        tabIndex={hidden ? -1 : 0}
                        className={`absolute inset-0 touch-none cursor-move overflow-hidden text-left focus-visible:ring-info focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none ${selected === i ? 'outline-signal outline-2' : overlapping.has(i) ? 'outline-warn outline-2 outline-dashed' : 'hover:outline-signal hover:outline-1'}`}
                        style={{
                          fontFamily: e.fontFamily ?? 'Arial',
                          fontSize: ((e.fontSize ?? 9) / 72) * scale,
                          fontWeight: e.bold ? 700 : 400,
                          fontStyle: e.italic ? 'italic' : 'normal',
                          textAlign: e.align?.toLowerCase() as
                            | 'left'
                            | 'center'
                            | 'right'
                            | undefined,
                          whiteSpace: e.wrap ? 'normal' : 'nowrap',
                          lineHeight: 1.15,
                          color: e.color || 'black',
                        }}
                        // Focus selects, so the arrow keys and the edit panel act on
                        // the same field (review row 10).
                        onFocus={() => setSelected(i)}
                        onClick={() => setSelected(i)}
                        onPointerDown={(event) => pointerDown(event, i, false)}
                        onPointerMove={pointerMove}
                        onPointerUp={endGesture}
                        onPointerCancel={endGesture}
                        onKeyDown={(event) => nudge(event, i)}
                      >
                        {e.type === 'Text' ? (
                          <PreviewText element={e} scale={scale} sample={sample} />
                        ) : e.type === 'Barcode' ? (
                          <span className="flex h-full flex-col">
                            <span
                              className="flex min-h-0 flex-1 items-center justify-center border border-dashed"
                              style={{ fontSize: 12 }}
                            >
                              Barcode
                            </span>
                            {e.barcodeHumanReadable ? (
                              <span
                                className="text-center font-mono leading-none"
                                style={{
                                  height: 0.15 * scale,
                                  fontSize: Math.min(0.12 * scale, 12),
                                }}
                              >
                                {values[e.barcodePayloadToken ?? 'PrimaryBarcode']}
                              </span>
                            ) : null}
                          </span>
                        ) : e.type === 'Image' ? (
                          <img
                            src={stationLogo}
                            alt=""
                            draggable={false}
                            className="pointer-events-none size-full object-contain"
                          />
                        ) : (
                          <span className="block h-full border" />
                        )}
                      </button>
                      {selected === i ? (
                        <button
                          type="button"
                          aria-label={`Resize ${fieldName(e)}`}
                          title="Drag to resize"
                          className="bg-signal border-surface absolute -right-2 -bottom-2 z-10 size-4 touch-none cursor-se-resize rounded-sm border"
                          onPointerDown={(event) => pointerDown(event, i, true)}
                          onPointerMove={pointerMove}
                          onPointerUp={endGesture}
                          onPointerCancel={endGesture}
                          onKeyDown={(event) => resizeByKey(event, e)}
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
            <p className="text-ink-muted mt-3 text-xs">
              Sample layout, enlarged for editing. Barcodes are placeholders; text fitting
              may differ on paper. Check a print from the station before using a new
              design.
            </p>
          </Panel>
          <Panel title="Use this at the label station">
            <p className="text-ink-muted text-sm">
              On the label station, open Settings, go to the <strong>Designer</strong> tab
              and choose <strong>Server templates</strong>. Save the settings, then press{' '}
              <strong>Sync</strong>. Press Sync again after each change here. Downloaded
              layouts keep working offline. Set up every label type the station prints: a
              type that isn&apos;t set up won&apos;t print.
            </p>
          </Panel>
        </div>
        <div className="min-w-0 space-y-4">
          <Panel title="Template">
            <Field label="Name" htmlFor="template-name">
              <Input
                id="template-name"
                maxLength={80}
                value={draft.template.name}
                onChange={(e) =>
                  change({
                    ...draft,
                    template: { ...draft.template, name: e.target.value },
                  })
                }
              />
            </Field>
          </Panel>
          {element && selected !== null ? (
            <Panel title={`Edit ${fieldName(element)}`}>
              <div className="space-y-3">
                {element.type === 'Text' ? (
                  <>
                    <Field label="Content" htmlFor="field-content">
                      <Select
                        id="field-content"
                        value={contentChoice}
                        onChange={(e) =>
                          patchElement({
                            text:
                              e.target.value === 'custom'
                                ? 'Your text'
                                : `{${e.target.value}}`,
                            visibleWhen:
                              e.target.value === 'WicBadge'
                                ? 'WicEligible'
                                : element.visibleWhen === 'WicEligible'
                                  ? null
                                  : element.visibleWhen,
                          })
                        }
                      >
                        {contentGroups(token).map((g) => (
                          <optgroup key={g.label} label={g.label}>
                            {g.choices.map((c) => (
                              <option key={c} value={c}>
                                {c === 'custom' ? 'Custom text' : FIELD_NAMES[c]}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </Select>
                    </Field>
                    {contentChoice === 'custom' ? (
                      <Field
                        label="Text"
                        htmlFor="custom-text"
                        error={
                          unknown.length
                            ? unknown.map((u) => `{${u}}`).join(', ') +
                              (unknown.length > 1
                                ? " aren't label fields."
                                : " isn't a label field.")
                            : null
                        }
                        hint="Insert a field to print its value, like Packed on {DateShort}."
                      >
                        <div className="flex gap-2">
                          <Input
                            id="custom-text"
                            ref={customText}
                            className="min-w-0 flex-1"
                            maxLength={2000}
                            invalid={unknown.length > 0}
                            value={element.text ?? ''}
                            onChange={(e) => patchElement({ text: e.target.value })}
                          />
                          <Select
                            aria-label="Insert field"
                            className="w-28"
                            value=""
                            onChange={(e) => {
                              const name = e.target.value;
                              if (!name) return;
                              const text = element.text ?? '';
                              const at =
                                customText.current?.selectionStart ?? text.length;
                              patchElement({
                                text: `${text.slice(0, at)}{${name}}${text.slice(at)}`,
                              });
                            }}
                          >
                            <option value="">Insert field…</option>
                            {LABEL_TOKENS.filter(
                              (t) => !['Sku', 'Barcode'].includes(t)
                            ).map((t) => (
                              <option key={t} value={t}>
                                {FIELD_NAMES[t]}
                              </option>
                            ))}
                          </Select>
                        </div>
                      </Field>
                    ) : token ? (
                      <p className="text-ink-muted text-xs">{sourceHint(token)}</p>
                    ) : null}
                    {contentChoice === 'ProductId' ? (
                      <Checkbox
                        label="Show # prefix"
                        checked={token === 'Sku'}
                        onCheckedChange={(prefix) =>
                          patchElement({ text: prefix ? '{Sku}' : '{ProductId}' })
                        }
                      />
                    ) : null}
                    <Field label="Text size" htmlFor="font-size" hint="4 to 96 pt">
                      <FontSizeInput
                        key={selected}
                        value={element.fontSize ?? 9}
                        onCommit={(fontSize) => patchElement({ fontSize })}
                        onPendingChange={setPendingSize}
                      />
                    </Field>
                    <Checkbox
                      label="Bold"
                      checked={element.bold ?? false}
                      onCheckedChange={(bold) => patchElement({ bold })}
                    />
                    <Field label="Alignment" htmlFor="text-align">
                      <Select
                        id="text-align"
                        value={element.align ?? 'Left'}
                        onChange={(e) =>
                          patchElement({ align: e.target.value as LabelElement['align'] })
                        }
                      >
                        <option>Left</option>
                        <option>Center</option>
                        <option>Right</option>
                      </Select>
                    </Field>
                    <Checkbox
                      label="Wrap onto multiple lines"
                      checked={element.wrap ?? false}
                      onCheckedChange={(wrap) => patchElement({ wrap })}
                    />
                    <Checkbox
                      label="Shrink text to fit"
                      checked={element.autoFit ?? false}
                      onCheckedChange={(autoFit) => patchElement({ autoFit })}
                    />
                  </>
                ) : element.type === 'Barcode' ? (
                  <>
                    <Field label="Barcode value" htmlFor="barcode-value">
                      <Select
                        id="barcode-value"
                        value={element.barcodePayloadToken ?? 'PrimaryBarcode'}
                        onChange={(e) =>
                          patchElement({
                            barcodePayloadToken: e.target
                              .value as LabelElement['barcodePayloadToken'],
                          })
                        }
                      >
                        <option value="PrimaryBarcode">Product barcode</option>
                        <option value="WeightedBarcode">Weighed-item barcode</option>
                        {element.barcodePayloadToken &&
                        !['PrimaryBarcode', 'WeightedBarcode'].includes(
                          element.barcodePayloadToken
                        ) ? (
                          <option value={element.barcodePayloadToken}>
                            Saved barcode field
                          </option>
                        ) : null}
                      </Select>
                    </Field>
                    <Field label="Barcode style" htmlFor="barcode-style">
                      <Select
                        id="barcode-style"
                        value={element.barcodeFormat ?? 'Auto'}
                        onChange={(e) =>
                          patchElement({
                            barcodeFormat: e.target
                              .value as LabelElement['barcodeFormat'],
                          })
                        }
                      >
                        <option value="Auto">Automatic</option>
                        <option value="Ean13">EAN-13 (bars)</option>
                        <option value="Qr">QR (square)</option>
                      </Select>
                    </Field>
                    <Checkbox
                      label="Print numbers under the barcode"
                      checked={element.barcodeHumanReadable ?? false}
                      onCheckedChange={(barcodeHumanReadable) =>
                        patchElement({
                          barcodeHumanReadable,
                          // Room for the digits under the bars (quick win 6).
                          height:
                            element.height +
                            (barcodeHumanReadable && !element.barcodeHumanReadable
                              ? 0.15
                              : !barcodeHumanReadable && element.barcodeHumanReadable
                                ? -0.15
                                : 0),
                        })
                      }
                    />
                  </>
                ) : null}
                <Field
                  label="Show this field"
                  htmlFor="field-visibility"
                  error={
                    neverPrints(element.visibleWhen, kind)
                      ? 'This rule is never true on this label type, so the field never prints.'
                      : null
                  }
                >
                  <Select
                    id="field-visibility"
                    value={currentRule}
                    onChange={(e) => {
                      const visibleWhen = e.target.value || null;
                      patchElement({ visibleWhen });
                      setSample((s) => sampleShowing(visibleWhen, s));
                    }}
                  >
                    {rules.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                    {currentRule && !rules.some((r) => r.value === currentRule) ? (
                      <option value={currentRule}>
                        Saved rule: {ruleWords(currentRule)}
                      </option>
                    ) : null}
                  </Select>
                </Field>
                <p className="text-ink-muted text-xs">
                  Move with the arrow keys on the preview. Hold Shift for a larger step.
                </p>
                <Button variant="ghost" onClick={removeSelected}>
                  Remove field
                </Button>
              </div>
            </Panel>
          ) : null}
          <Panel title="Fields">
            <div className="space-y-1">
              {fieldListOrder(elements).map((i, n, order) => {
                const e = elements[i]!;
                const words = ruleWords(e.visibleWhen);
                const partnered =
                  n > 0 &&
                  !!e.visibleWhen &&
                  elements[order[n - 1]!]!.type === e.type &&
                  (elements[order[n - 1]!]!.visibleWhen === `!${e.visibleWhen}` ||
                    e.visibleWhen === `!${elements[order[n - 1]!]!.visibleWhen}`);
                return (
                  <button
                    key={i}
                    type="button"
                    className={`block w-full rounded px-2 py-1.5 text-left text-sm ${partnered ? 'pl-5' : ''} ${i === selected ? 'bg-signal-muted text-signal-ink' : 'hover:bg-surface-hover'}`}
                    onClick={() => select(i)}
                  >
                    {fieldName(e)}
                    {words ? (
                      <span className="text-ink-muted ml-1 text-xs">· {words}</span>
                    ) : null}
                    {neverPrints(e.visibleWhen, kind) ? (
                      <span className="text-warn ml-1 text-xs">· never prints here</span>
                    ) : null}
                    {overlapping.has(i) ? (
                      <span className="text-warn ml-1 text-xs">· overlaps</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </Panel>
          <Panel title="Version history">
            {history.isLoading ? (
              <Skeleton className="h-16" />
            ) : history.isError ? (
              <p className="text-negative text-sm">{errorMessage(history.error)}</p>
            ) : history.data?.revisions.length ? (
              <ul className="space-y-1" aria-label="Saved versions">
                {history.data.revisions.map((r) => (
                  <li key={r.revision} className="flex items-center gap-2 text-sm">
                    <div className="min-w-0 flex-1">
                      <div className="truncate">
                        {dateTime(r.savedAt)}
                        {r.current ? (
                          <Badge tone="positive" className="ml-2">
                            Live
                          </Badge>
                        ) : null}
                      </div>
                      <div className="text-ink-muted truncate text-xs">
                        {r.savedBy ?? 'Unknown'}
                        {r.restoredFrom ? ' · restored an older version' : ''}
                        {!r.readable ? ' · no longer usable' : ''}
                      </div>
                    </div>
                    {!r.current ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={!canEdit || !r.readable || busy}
                        onClick={() => setRestoreTarget(r)}
                      >
                        Restore
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-ink-muted text-sm">No saved versions yet.</p>
            )}
          </Panel>
        </div>
      </fieldset>
      <ConfirmDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        title="Start over with the sample layout?"
        description="Your saved template stays in place until you save again. You can undo this change."
        confirmLabel="Start over"
        variant="default"
        onConfirm={() => {
          change(defaultLabelDesign(kind));
          setSelected(0);
          setResetOpen(false);
        }}
      />
      <ConfirmDialog
        open={reloadOpen}
        onOpenChange={setReloadOpen}
        title="Reload the saved template?"
        description="This replaces your unsaved edits with the latest server version."
        confirmLabel="Reload"
        loading={reload.isPending}
        onConfirm={() => reload.mutate()}
      />
      <ConfirmDialog
        open={restoreTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRestoreTarget(null);
        }}
        title={
          restoreTarget
            ? `Restore the version saved ${dateTime(restoreTarget.savedAt)}?`
            : 'Restore this version?'
        }
        description={`It becomes the live ${LABEL_TITLES[kind]} layout, and stations get it at their next Sync. The current version stays in the history.${dirty ? ' Your unsaved changes are discarded.' : ''}`}
        confirmLabel="Restore"
        variant="default"
        loading={restore.isPending}
        onConfirm={() => {
          if (restoreTarget) restore.mutate(restoreTarget);
        }}
      />
      <Dialog
        open={sizeDialog !== null}
        onOpenChange={(open) => {
          if (!open) setSizeDialog(null);
        }}
        title="Change the label size"
        description="Must match the labels loaded in the printer."
        footer={
          <>
            <Button variant="ghost" onClick={() => setSizeDialog(null)}>
              Cancel
            </Button>
            <Button
              disabled={
                !sizeDialog ||
                parsePaperInches(sizeDialog.w) === null ||
                parsePaperInches(sizeDialog.h) === null
              }
              onClick={() => changeSize('keep')}
            >
              Keep field sizes
            </Button>
            <Button
              variant="primary"
              disabled={
                !sizeDialog ||
                parsePaperInches(sizeDialog.w) === null ||
                parsePaperInches(sizeDialog.h) === null
              }
              onClick={() => changeSize('stretch')}
            >
              Stretch fields
            </Button>
          </>
        }
      >
        {sizeDialog ? (
          <div className="space-y-3">
            <div className="flex gap-3">
              <Field
                label="Width (in)"
                htmlFor="paper-width"
                error={parsePaperInches(sizeDialog.w) === null ? '1 to 12 inches' : null}
              >
                <Input
                  id="paper-width"
                  inputMode="decimal"
                  value={sizeDialog.w}
                  onChange={(e) => setSizeDialog({ ...sizeDialog, w: e.target.value })}
                />
              </Field>
              <Field
                label="Height (in)"
                htmlFor="paper-height"
                error={parsePaperInches(sizeDialog.h) === null ? '1 to 12 inches' : null}
              >
                <Input
                  id="paper-height"
                  inputMode="decimal"
                  value={sizeDialog.h}
                  onChange={(e) => setSizeDialog({ ...sizeDialog, h: e.target.value })}
                />
              </Field>
            </div>
            <p className="text-ink-muted text-xs">
              Keep field sizes moves fields back onto the label if they would fall off.
              Stretch fields scales every field with the label; text sizes stay the same.
            </p>
          </div>
        ) : null}
      </Dialog>
      <ConfirmDialog
        open={blocker.status === 'blocked'}
        onOpenChange={(open) => {
          if (!open && blocker.status === 'blocked') blocker.reset();
        }}
        title="Leave without saving?"
        description="Your template has unsaved changes."
        confirmLabel="Leave without saving"
        onConfirm={() => {
          if (blocker.status === 'blocked') blocker.proceed();
        }}
      />
    </div>
  );

  function nudge(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const delta = event.shiftKey ? 0.1 : 0.01;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-delta, 0],
      ArrowRight: [delta, 0],
      ArrowUp: [0, -delta],
      ArrowDown: [0, delta],
    };
    const move = moves[event.key];
    if (!move) return;
    event.preventDefault();
    change({
      ...draft,
      template: {
        ...draft.template,
        elements: elements.map((el, n) =>
          n === index
            ? boundElement({ ...el, x: el.x + move[0], y: el.y + move[1] }, draft)
            : el
        ),
      },
    });
  }

  function resizeByKey(event: KeyboardEvent<HTMLButtonElement>, e: LabelElement) {
    const d = event.shiftKey ? 0.1 : 0.01;
    const dWidth = event.key === 'ArrowRight' ? d : event.key === 'ArrowLeft' ? -d : 0;
    const dHeight = event.key === 'ArrowDown' ? d : event.key === 'ArrowUp' ? -d : 0;
    if (!dWidth && !dHeight) return;
    event.preventDefault();
    if (e.type === 'Image') {
      const next = resizeLogo(e, dWidth, dHeight, draft);
      patchElement({ width: next.width, height: next.height });
    } else patchElement({ width: e.width + dWidth, height: e.height + dHeight });
  }
}

/** Text size box: keeps what is typed while it has focus and applies it, clamped
 *  to 4–96 in half points, on Enter or when focus leaves (review row 6). The arrow
 *  keys step by half a point, or 2 with Shift, and apply at once. */
function FontSizeInput({
  value,
  onCommit,
  onPendingChange,
}: {
  value: number;
  onCommit: (size: number) => void;
  /** True while typed text would change the size once applied. */
  onPendingChange: (pending: boolean) => void;
}) {
  const [text, setText] = useState<string | null>(null);
  useEffect(() => () => onPendingChange(false), [onPendingChange]);
  function edit(next: string | null) {
    setText(next);
    const size = next === null ? null : parseFontSize(next);
    onPendingChange(size !== null && size !== value);
  }
  function commit() {
    if (text === null) return;
    const size = parseFontSize(text);
    edit(null);
    if (size !== null && size !== value) onCommit(size);
  }
  return (
    <Input
      id="font-size"
      type="number"
      inputMode="decimal"
      min={4}
      max={96}
      step={0.5}
      value={text ?? String(value)}
      onChange={(e) => edit(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          edit(null);
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
          const base = parseFontSize(text ?? String(value)) ?? value;
          const step = (e.shiftKey ? 2 : 0.5) * (e.key === 'ArrowUp' ? 1 : -1);
          const size = parseFontSize(String(base + step));
          edit(null);
          if (size !== null && size !== value) onCommit(size);
        }
      }}
    />
  );
}

function PreviewText({
  element,
  scale,
  sample,
}: {
  element: LabelElement;
  scale: number;
  sample: Sample;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const text = sampleText(element, sample);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const px = (points: number) => `${(points / 72) * scale}px`;
    let points = element.fontSize ?? 9;
    node.style.fontSize = px(points);
    if (element.autoFit) {
      // The station's rule (TASK-0221 TextFit): wrap, then shrink in 0.5 pt steps
      // down to 5 pt, or to the field's own size if that is already smaller.
      const floor = textFitFloor(element.fontSize ?? 9);
      while (
        points > floor &&
        (node.scrollHeight > node.clientHeight + 1 ||
          node.scrollWidth > node.clientWidth + 1)
      ) {
        points = nextFitSize(points, floor);
        node.style.fontSize = px(points);
      }
    }
  }, [element, scale, text]);
  return (
    <span ref={ref} className="block h-full w-full overflow-hidden">
      {text}
    </span>
  );
}
